import {
  CalendarMonth,
  CardGiftcard,
  EmojiEvents,
  History,
  KeyboardDoubleArrowRight,
  Logout,
  Map,
  Settings,
  SupervisorAccount,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  Stack,
  Tooltip,
} from "@mui/joy";
import { RegisteredRouter, ValidateToPath } from "@tanstack/react-router";
import { ReactNode } from "react";

import Challenge from "@/assets/icons/Challenge";
import { ListItemButtonLink } from "@/components/Links";
import useUiState from "@/core/ui";

import AddNewButton from "./AddNewButton";
import ClimbTypeSelector from "./ClimbTypeSelector";
import { CollapseText, SidebarExpandedContext } from "./SidebarExpanded";
import VenueSwitcher from "./VenueSwitcher";

const NavItem = <TTo extends string>({
  to,
  icon,
  label,
}: {
  to: ValidateToPath<RegisteredRouter, TTo, "/gym/$venueUuid">;
  icon: ReactNode;
  label: string;
}) => (
  <ListItem>
    <ListItemButtonLink
      to={to}
      from="/gym/$venueUuid"
      params={true}
      activeProps={{ selected: true }}
    >
      <ListItemDecorator>{icon}</ListItemDecorator>
      <CollapseText>{label}</CollapseText>
    </ListItemButtonLink>
  </ListItem>
);

export default function MainMenu() {
  const { sidebarExpanded: isExpanded, setSidebarExpanded: setIsExpanded } = useUiState();

  const onLogout = () => {
    localStorage.removeItem("auth.token");
    window.location.reload();
  };

  return (
    <SidebarExpandedContext.Provider value={isExpanded}>
      <Stack
        sx={{
          width: isExpanded ? 240 : 60,
          transition: "width 0.3s",
          ".MuiListItem-root": {
            fontSize: "md",
            ".Mui-selected": {
              color: "var(--joy-palette-primary-blue2)",
              backgroundColor: "transparent!important",
              fontWeight: "bold!important",
              gap: 0,
            },
            a: {
              fontWeight: "bold",
              paddingLeft: 2,
              gap: 0,
            },
            "a:hover": {
              backgroundColor: "var(--joy-palette-primary-100)!important",
            },
            ".MuiListItemDecorator-root": {
              marginInlineEnd: "-16px",
            },
          },
          flex: 1,
          height: "100%",
        }}
      >
        <VenueSwitcher />
        <List size="lg" sx={{ display: "flex", flex: 1 }}>
          <ClimbTypeSelector />
          <NavItem to="/gym/$venueUuid/routes" icon={<Map />} label="Routes" />
          <NavItem to="/gym/$venueUuid/archive" icon={<History />} label="Archive" />
          <NavItem to="/gym/$venueUuid/schedule" icon={<CalendarMonth />} label="Schedule" />
          <NavItem to="/gym/$venueUuid/events" icon={<EmojiEvents />} label="Events" />
          <NavItem to="/gym/$venueUuid/challenges" icon={<Challenge />} label="Challenges" />
          <NavItem to="/gym/$venueUuid/loyalty" icon={<CardGiftcard />} label="Loyalty" />
          <NavItem to="/gym/$venueUuid/staff" icon={<SupervisorAccount />} label="Staff" />
          <Box sx={{ flex: 1 }} />
          <NavItem to="/gym/$venueUuid/settings" icon={<Settings />} label="Settings" />

          <AddNewButton isExpanded={isExpanded} />
          <ListItem sx={{ justifyContent: "space-between" }}>
            <ListItemButton onClick={() => setIsExpanded(!isExpanded)}>
              <ListItemDecorator
                sx={{
                  svg: {
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s",
                  },
                }}
              >
                <KeyboardDoubleArrowRight />
              </ListItemDecorator>
            </ListItemButton>
            {isExpanded ? (
              <Tooltip title="Log out">
                <IconButton size="sm" onClick={onLogout}>
                  <Logout />
                </IconButton>
              </Tooltip>
            ) : null}
          </ListItem>
        </List>
      </Stack>
    </SidebarExpandedContext.Provider>
  );
}
